import React, { useState } from 'react';
import {
  Box,
  Typography,
  TextField,
  InputAdornment,
  Skeleton,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Paper,
  Card,
} from '@mui/material';
import {
  Search as SearchIcon,
  Assessment as AssessmentIcon,
} from '@mui/icons-material';
import { useNavigate } from 'react-router-dom';
import { useApiGet } from '../../utils/httpClientTs';
import { getPEOReportList } from '../../endpoints/peoReportsTs';
import { PEOReport } from '../../types/peoReports';
import AlertItem from '../../Components/AlertItem';

const PEOReportsList: React.FC = () => {
  const navigate = useNavigate();
  const [searchQuery, setSearchQuery] = useState('');
  const { data, isLoading, error } = useApiGet<{ reports: PEOReport[] }>(
    ['peo-reports-list'],
    getPEOReportList,
    {
      enabled: true,
      retry: 1,
    }
  );

  const reports = data?.reports || [];

  const filteredReports = reports.filter((report) => {
    const query = searchQuery.toLowerCase();
    return (
      report.name.toLowerCase().includes(query) ||
      report.description.toLowerCase().includes(query) ||
      report.group_type_name.toLowerCase().includes(query)
    );
  });

  const sortedReports = [...filteredReports].sort((a, b) =>
    a.name.localeCompare(b.name, undefined, { sensitivity: 'base' })
  );

  return (
    <Box sx={{ p: 3 }}>
      {/* Header */}
      <Box sx={{ mb: 4 }}>
        <Typography variant="h4" gutterBottom sx={{ fontWeight: 600 }}>
          Reports
        </Typography>
        <Typography variant="body1" color="text.secondary">
          Select a report to view and download data
        </Typography>
      </Box>

      {/* Search */}
      <TextField
        fullWidth
        size="small"
        placeholder="Search reports by name, description, or group..."
        value={searchQuery}
        onChange={(e) => setSearchQuery(e.target.value)}
        InputProps={{
          startAdornment: (
            <InputAdornment position="start">
              <SearchIcon />
            </InputAdornment>
          ),
        }}
        sx={{ mb: 3, maxWidth: 600 }}
      />

      {/* Loading State */}
      {isLoading && (
        <Box sx={{ maxWidth: 800 }}>
          <Skeleton variant="rectangular" height={48} sx={{ mb: 0.5 }} />
          {[1, 2, 3, 4, 5, 6, 7, 8].map((n) => (
            <Skeleton
              key={n}
              variant="rectangular"
              height={52}
              sx={{ mb: 0.5 }}
            />
          ))}
        </Box>
      )}

      {/* Error State */}
      {error && (
        <AlertItem
          severity="error"
          description="Failed to load reports. Please try again later."
          onClose={() => {}}
          keepOpen={true}
        />
      )}

      {/* Reports Table */}
      {!isLoading && !error && (
        <>
          {filteredReports.length === 0 ? (
            <Card sx={{ p: 4, textAlign: 'center' }}>
              <AssessmentIcon
                sx={{ fontSize: 64, color: 'text.secondary', mb: 2 }}
              />
              <Typography variant="h6" color="text.secondary" gutterBottom>
                {reports.length === 0
                  ? 'No Reports Available'
                  : 'No Reports Found'}
              </Typography>
              <Typography variant="body2" color="text.secondary">
                {reports.length === 0
                  ? 'You do not have access to any reports at this time.'
                  : 'Try adjusting your search query.'}
              </Typography>
            </Card>
          ) : (
            <TableContainer
              component={Paper}
              variant="outlined"
              sx={{ maxWidth: 800 }}
            >
              <Table size="medium">
                <TableHead>
                  <TableRow>
                    <TableCell sx={{ fontWeight: 600 }}>Report</TableCell>
                    <TableCell sx={{ fontWeight: 600 }}>Description</TableCell>
                  </TableRow>
                </TableHead>
                <TableBody>
                  {sortedReports.map((report) => (
                    <TableRow
                      key={report.id}
                      hover
                      sx={{
                        cursor: 'pointer',
                        '&:last-child td': { borderBottom: 0 },
                      }}
                      onClick={() => navigate(`/peo-reports/${report.id}`)}
                    >
                      <TableCell sx={{ fontWeight: 500 }}>
                        {report.name}
                      </TableCell>
                      <TableCell
                        sx={{
                          maxWidth: 400,
                          overflow: 'hidden',
                          textOverflow: 'ellipsis',
                          whiteSpace: 'nowrap',
                          color: 'text.secondary',
                        }}
                      >
                        {report.description || '—'}
                      </TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
            </TableContainer>
          )}
        </>
      )}
    </Box>
  );
};

export default PEOReportsList;
